import React, { useEffect, useState } from 'react';
import Popup from '../../../../../components/general/Popup.tsx';
import { Input } from '../../../../../components/general/Input.tsx';
import MoneyInput from '../../../../../components/general/MoneyInput/index.ts';
import Dropdown from '../../../../../components/general/Dropdown.tsx';
import Form from '../../../../../components/form-library/index.ts';
import { DropdownVATOptions, VAT_DROPDOWN_OPTIONS } from '../../../../../types/enums.ts';
import { centsToLocalizedString, isMoney, toCentsFromMoney, toMoneyFromCents } from '../../../../../lib/Money.ts';
import { DEFAULT_CURRENCY } from '../../../../../types/constants.ts';
import {
    CatalogItem,
    CatalogItemFormValues,
    CATALOG_UNIT_OPTIONS,
    computePriceIncludingVatCents,
} from './types.ts';
import type {Unit} from '../../../../../generated/enums.ts';

const EMPTY_FORM: CatalogItemFormValues = {
    name: '',
    unit_price: null,
    unit: 'hour',
    vat_option: DropdownVATOptions.STANDARD,
};

interface CatalogItemModalProps {
    open: boolean;
    item: CatalogItem | null;
    takenNames: string[];
    onSubmit: (values: CatalogItemFormValues) => Promise<void>;
    onClose: () => void;
}

export default function CatalogItemModal({ open, item, takenNames, onSubmit, onClose }: CatalogItemModalProps) {
    const [form] = Form.useForm<CatalogItemFormValues>();
    const [isSaving, setIsSaving] = useState(false);
    const [priceInclVatCents, setPriceInclVatCents] = useState<number | null>(null);

    const isEditing = item !== null;

    useEffect(() => {
        setIsSaving(false);
        if (!open) {
            return;
        }
        if (item) {
            form.setFieldsValue({
                name: item.name,
                unit_price: toMoneyFromCents(item.unit_price_cents, item.currency || DEFAULT_CURRENCY),
                unit: item.unit,
                vat_option: item.vat_option,
            });
            setPriceInclVatCents(computePriceIncludingVatCents(item.unit_price_cents, item.vat_option));
        } else {
            form.setFieldsValue({ ...EMPTY_FORM });
            setPriceInclVatCents(null);
        }
    }, [open, item, form]);

    function updateInclVatPreview(values: CatalogItemFormValues) {
        if (isMoney(values.unit_price)) {
            setPriceInclVatCents(
                computePriceIncludingVatCents(toCentsFromMoney(values.unit_price), values.vat_option),
            );
        } else {
            setPriceInclVatCents(null);
        }
    }

    const handleFinish = async (values: CatalogItemFormValues) => {
        setIsSaving(true);
        try {
            await onSubmit(values);
            form.resetFields();
        } catch {
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Popup
            open={open}
            onClose={onClose}
            onConfirm={() => form.submit()}
            title={isEditing ? 'Item bewerken' : 'Nieuw item'}
            confirmButtonDisplayLabel="Opslaan"
            confirmButtonDisabled={isSaving}
            Icon={null}
        >
            <p className="text-sm text-gray-500 mt-1">
                Standaardwaarden die je bij het factureren direct kunt invullen.
            </p>
            <Form
                form={form}
                className="py-2"
                initialValues={EMPTY_FORM}
                onFinish={(v) => void handleFinish(v)}
                onValuesChange={(_, values) => updateInclVatPreview(values)}
            >
                <Form.Item
                    name="name"
                    label="Naam"
                    rules={[
                        { required: true, message: 'Vul een naam in.' },
                        {
                            message: 'Er bestaat al een item met deze naam.',
                            validator: (_, value) => {
                                const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
                                if (typeof value === 'string' && value.length > 0 && normalized === '') {
                                    throw new Error('Vul een naam in.');
                                }
                                if (normalized !== '' && takenNames.includes(normalized)) {
                                    throw new Error('Er bestaat al een item met deze naam.');
                                }
                            },
                        },
                    ]}
                >
                    <Input type="text" autoComplete="off" placeholder="Bijv. Strategisch advies" />
                </Form.Item>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Form.Item
                            name="unit_price"
                            label="Stukprijs (excl. btw)"
                            rules={[{ required: true, message: 'Vul een stukprijs in.' }]}
                        >
                            <MoneyInput locale="nl-NL" currency={DEFAULT_CURRENCY} />
                        </Form.Item>
                        {priceInclVatCents !== null && (
                            <p className="mt-1 text-xs text-gray-400">
                                € {centsToLocalizedString(priceInclVatCents)} incl. btw
                            </p>
                        )}
                    </div>

                    <Form.Item name="unit" label="Eenheid">
                        {({ value }) => (
                            <Dropdown
                                selected={
                                    CATALOG_UNIT_OPTIONS.find((option) => option.value === value) ??
                                    CATALOG_UNIT_OPTIONS[0]
                                }
                                options={CATALOG_UNIT_OPTIONS}
                                onUpdateFunc={(v) => {
                                    form.setFieldValue('unit', v as Unit);
                                }}
                            />
                        )}
                    </Form.Item>
                </div>

                <Form.Item name="vat_option" label="Btw-tarief">
                    {({ value }) => (
                        <Dropdown
                            selected={
                                VAT_DROPDOWN_OPTIONS.find((option) => option.value === value) ??
                                VAT_DROPDOWN_OPTIONS[0]
                            }
                            options={VAT_DROPDOWN_OPTIONS}
                            onUpdateFunc={(v) => {
                                form.setFieldValue('vat_option', v as DropdownVATOptions);
                                updateInclVatPreview(form.getFieldsValue(true));
                            }}
                        />
                    )}
                </Form.Item>
            </Form>
        </Popup>
    );
}
